// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, CheckCircle, XCircle, ChevronRight, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  {
    id: 0,
    q: "Cosa indica la voce 'Retribuzione base' nella busta paga?",
    opts: [
      "Il totale delle ore straordinarie del mese",
      "Lo stipendio fisso mensile per il tuo livello CCNL",
      "Il netto da pagare che arriva sul conto",
      "Il TFR accantonato nel mese",
    ],
    correct: 1,
    explanation: "La Retribuzione base è lo stipendio fisso mensile stabilito dal tuo livello nel CCNL (Contratto Collettivo Nazionale di Lavoro). Non include straordinari o indennità variabili.",
  },
  {
    id: 1,
    q: "Qual è la percentuale di contributi INPS a carico del dipendente nel 2026?",
    opts: ["6,91%", "9,19%", "13,50%", "23,80%"],
    correct: 1,
    explanation: "Il dipendente versa il 9,19% della retribuzione lorda all'INPS per la pensione futura. Il datore di lavoro versa ulteriori ~23,80% che non appaiono in busta paga — il totale è il cuneo fiscale contributivo.",
  },
  {
    id: 2,
    q: "Cosa succede con l'IRPEF in busta paga a dicembre?",
    opts: [
      "Viene azzerata perché è l'ultimo mese",
      "Viene applicata un'aliquota extra del 5%",
      "Viene ricalcolata a conguaglio sull'intero anno",
      "Non cambia rispetto agli altri mesi",
    ],
    correct: 2,
    explanation: "A dicembre il datore di lavoro effettua il conguaglio IRPEF: confronta le ritenute mensili applicate durante l'anno con l'imposta effettiva dovuta. Se hai pagato troppo, ricevi un rimborso in busta paga. Se troppo poco, viene trattenuto il saldo.",
  },
  {
    id: 3,
    q: "Quanti giorni di ferie minime garantisce la legge italiana per un lavoratore full-time?",
    opts: [
      "10 giorni lavorativi (2 settimane)",
      "15 giorni lavorativi (3 settimane)",
      "20 giorni lavorativi (4 settimane)",
      "25 giorni lavorativi (5 settimane)",
    ],
    correct: 2,
    explanation: "L'art. 10 del D.Lgs. 66/2003 garantisce un minimo di 4 settimane di ferie annuali (20 giorni lavorativi per chi lavora 5 giorni a settimana). Molti CCNL prevedono un numero maggiore. La legge impone che almeno 2 settimane siano godute nell'anno di maturazione.",
  },
  {
    id: 4,
    q: "Il TFR mensile si calcola come:",
    opts: [
      "Stipendio lordo mensile ÷ 12",
      "Stipendio lordo mensile ÷ 13,5",
      "Stipendio netto mensile ÷ 12",
      "Stipendio lordo mensile × 1,5%",
    ],
    correct: 1,
    explanation: "Il TFR mensile = Retribuzione lorda ÷ 13,5. Su base annua, il TFR accantonato è circa 1 mensilità lorda. Su 13,5 mesi lavorati si accumula un mese di TFR. Ogni anno matura 7,41% della retribuzione annua lorda.",
  },
];

export default function QuizEspertoBustaPaga() {
  const { user } = useAuth();
  const [phase, setPhase] = useState('intro'); // intro | quiz | result
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyBadge, setAlreadyBadge] = useState(false);

  useEffect(() => {
    if (user?.badges?.includes('esperto_busta_paga')) setAlreadyBadge(true);
  }, [user]);

  const handleSelect = (optIdx) => {
    if (showFeedback) return;
    setSelectedOpt(optIdx);
    setShowFeedback(true);
  };

  const handleNext = () => {
    const newAnswers = [...answers, selectedOpt];
    setAnswers(newAnswers);
    setSelectedOpt(null);
    setShowFeedback(false);
    if (currentQ + 1 < QUESTIONS.length) {
      setCurrentQ(q => q + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/quiz/esperto-busta-paga/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: finalAnswers }),
      });
      const data = await res.json();
      setResult(data);
      setPhase('result');
    } catch {
      setResult({ correct: finalAnswers.filter((a, i) => a === QUESTIONS[i].correct).length, total: 5, passed: false, badge_awarded: false, message: 'Errore nel salvataggio. Conteggio locale applicato.' });
      setPhase('result');
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setPhase('quiz');
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOpt(null);
    setShowFeedback(false);
    setResult(null);
  };

  const q = QUESTIONS[currentQ];
  const isCorrect = showFeedback && selectedOpt === q?.correct;

  return (
    <div className="min-h-screen bg-[#1C1C1A]" data-testid="quiz-esperto-page">
      <div className="container-wide py-10 max-w-2xl">
        <AnimatePresence mode="wait">
          {phase === 'intro' && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.15)', border: '2px solid rgba(201,168,76,0.4)' }}>
                  <Trophy className="w-10 h-10 text-[#C9A84C]" />
                </div>
                <h1 className="text-3xl font-bold text-[#E8E8E4] mb-3">Quiz: Esperto di Busta Paga</h1>
                <p className="text-[#888780]" style={{ lineHeight: 1.7, fontSize: 15 }}>
                  Metti alla prova le tue conoscenze sulla busta paga.<br />
                  Rispondi correttamente ad almeno <strong className="text-[#C9A84C]">4 domande su 5</strong> per ottenere il badge.
                </p>
              </div>

              {alreadyBadge && (
                <div className="mb-6 p-4 rounded-xl text-center" style={{ background: 'rgba(61,122,82,0.1)', border: '1px solid rgba(61,122,82,0.3)' }}>
                  <CheckCircle className="w-6 h-6 text-[#3D7A52] mx-auto mb-2" />
                  <p className="text-[#3D7A52] font-semibold">Hai già il badge Esperto di Busta Paga!</p>
                  <p className="text-sm text-[#888780] mt-1">Puoi rifarlo per migliorare la tua conoscenza.</p>
                </div>
              )}

              <div className="sapiens-card p-6 mb-6">
                <p className="text-sm font-semibold text-[#888780] uppercase tracking-wider mb-4">Il quiz include:</p>
                <div className="space-y-3">
                  {QUESTIONS.map((q, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}>{i + 1}</div>
                      <p className="text-sm text-[#888780]">{q.q}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setPhase('quiz')}
                className="w-full py-4 rounded-xl font-bold text-base sapiens-btn-primary flex items-center justify-center gap-2"
                data-testid="start-quiz-btn"
              >
                Inizia il quiz <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {phase === 'quiz' && q && (
            <motion.div key={`q-${currentQ}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              {/* Progress */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-[#888780]">Domanda {currentQ + 1} di {QUESTIONS.length}</span>
                <div className="flex gap-1.5">
                  {QUESTIONS.map((_, i) => (
                    <div key={i} style={{ width: 24, height: 4, borderRadius: 2, background: i < currentQ ? '#3D7A52' : i === currentQ ? '#C9A84C' : 'rgba(255,255,255,0.1)' }} />
                  ))}
                </div>
              </div>

              <div className="sapiens-card p-6 mb-4">
                <p className="text-lg font-semibold text-[#E8E8E4] mb-6" style={{ lineHeight: 1.5 }} data-testid={`question-${currentQ}`}>{q.q}</p>
                <div className="space-y-3">
                  {q.opts.map((opt, i) => {
                    let bg = 'rgba(255,255,255,0.03)';
                    let border = 'rgba(255,255,255,0.08)';
                    let color = '#888780';
                    if (showFeedback) {
                      if (i === q.correct) { bg = 'rgba(61,122,82,0.15)'; border = '#3D7A52'; color = '#3D7A52'; }
                      else if (i === selectedOpt && i !== q.correct) { bg = 'rgba(232,112,64,0.12)'; border = '#E87040'; color = '#E87040'; }
                    } else if (i === selectedOpt) {
                      bg = 'rgba(201,168,76,0.1)'; border = 'rgba(201,168,76,0.5)'; color = '#C9A84C';
                    }
                    return (
                      <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        disabled={showFeedback}
                        data-testid={`option-${i}`}
                        style={{ width: '100%', textAlign: 'left', padding: '14px 16px', borderRadius: 10, background: bg, border: `1px solid ${border}`, color, fontSize: 14, cursor: showFeedback ? 'default' : 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 10 }}
                      >
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: `${border}22`, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{['A','B','C','D'][i]}</span>
                        {opt}
                        {showFeedback && i === q.correct && <CheckCircle className="w-4 h-4 ml-auto" style={{ color: '#3D7A52' }} />}
                        {showFeedback && i === selectedOpt && i !== q.correct && <XCircle className="w-4 h-4 ml-auto" style={{ color: '#E87040' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="mb-4 p-4 rounded-xl text-sm"
                    style={{ background: isCorrect ? 'rgba(61,122,82,0.1)' : 'rgba(232,112,64,0.1)', border: `1px solid ${isCorrect ? 'rgba(61,122,82,0.3)' : 'rgba(232,112,64,0.3)'}` }}
                  >
                    <p className="font-semibold mb-1" style={{ color: isCorrect ? '#3D7A52' : '#E87040' }}>
                      {isCorrect ? 'Corretto!' : 'Sbagliato'}
                    </p>
                    <p className="text-[#888780]">{q.explanation}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {showFeedback && (
                <button
                  onClick={handleNext}
                  disabled={submitting}
                  className="w-full py-3 rounded-xl font-semibold sapiens-btn-primary flex items-center justify-center gap-2"
                  data-testid="next-question-btn"
                >
                  {submitting ? 'Calcolo...' : currentQ + 1 < QUESTIONS.length ? 'Prossima domanda' : 'Vedi risultato'}
                  {!submitting && <ChevronRight className="w-4 h-4" />}
                </button>
              )}
            </motion.div>
          )}

          {phase === 'result' && result && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <div
                  className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ background: result.passed ? 'rgba(201,168,76,0.15)' : 'rgba(232,112,64,0.1)', border: `2px solid ${result.passed ? '#C9A84C' : '#E87040'}` }}
                >
                  {result.passed ? <Trophy className="w-12 h-12 text-[#C9A84C]" /> : <AlertTriangle className="w-12 h-12 text-[#E87040]" />}
                </div>
                <h2 className="text-3xl font-bold text-[#E8E8E4] mb-3">
                  {result.correct}/{result.total} corrette
                </h2>
                <p className="text-[#888780]" style={{ fontSize: 15, lineHeight: 1.6 }}>{result.message}</p>
              </div>

              {result.badge_awarded && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  className="mb-6 p-5 rounded-2xl text-center"
                  style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.15) 0%, rgba(61,122,82,0.1) 100%)', border: '1px solid rgba(201,168,76,0.4)' }}
                  data-testid="badge-awarded-banner"
                >
                  <p className="text-2xl mb-2">🏆</p>
                  <p className="font-bold text-[#C9A84C] text-lg">Badge sbloccato!</p>
                  <p className="text-sm text-[#888780] mt-1">Esperto di Busta Paga aggiunto al tuo profilo</p>
                </motion.div>
              )}

              {/* Score breakdown */}
              <div className="sapiens-card p-5 mb-6">
                {QUESTIONS.map((q, i) => {
                  const correct = answers[i] === q.correct;
                  return (
                    <div key={i} className="flex items-start gap-3 py-3 border-b border-white/[0.04] last:border-0" data-testid={`result-q-${i}`}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: correct ? 'rgba(61,122,82,0.2)' : 'rgba(232,112,64,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {correct ? <CheckCircle className="w-3.5 h-3.5 text-[#3D7A52]" /> : <XCircle className="w-3.5 h-3.5 text-[#E87040]" />}
                      </div>
                      <div>
                        <p className="text-sm text-[#888780]">{q.q}</p>
                        {!correct && <p className="text-xs text-[#3D7A52] mt-0.5">Risposta corretta: {q.opts[q.correct]}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={restart}
                  className="flex-1 py-3 rounded-xl font-medium sapiens-btn-secondary flex items-center justify-center gap-2"
                  data-testid="retry-quiz-btn"
                >
                  <RotateCcw className="w-4 h-4" /> Riprova
                </button>
                <a
                  href="/dashboard"
                  className="flex-1 py-3 rounded-xl font-medium sapiens-btn-primary flex items-center justify-center gap-2 no-underline"
                  data-testid="go-dashboard-btn"
                >
                  Vai al profilo <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
